<template>
  <div class="map-container">
    <div v-if="noCoordinates" class="no-position-overlay-text">{{ $t('lMap:noCoordinates') | capitalize }}</div>
    <div v-if="noCoordinates" class="no-position-overlay" />

    <l-map ref="map" :bounds="bounds">
      <l-tile-layer :url="tileUrl" :attribution="tileAttribution" />

      <!-- Slot for markers or clusters -->
      <slot />
    </l-map>
  </div>
</template>

<script>
import { isEqual, each, isArray } from 'lodash-es'
import constants from '@/configuration/constants'

import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import { LMap, LTileLayer } from 'vue2-leaflet'
import { mapGetters } from 'vuex'

export default {
  components: { LMap, LTileLayer },

  props: {
    // List of object that will be displayed on map
    // Needed to calculate zoom and bounds
    markers: {
      type: Array,
      required: true,
      default: () => []
    }
  },

  data () {
    return {
      // Default values
      bounds: null,
      noCoordinates: true
    }
  },

  computed: {
    // mix the getters into computed with object spread operator
    ...mapGetters(['dashboardSettings']),

    tileUrl () {
      return this.dashboardSettings.tileServerUrl || constants.tilesServerUrl
    },

    tileAttribution () {
      return this.dashboardSettings.tileServerAttribution || constants.tilesServerAttribution
    }
  },

  watch: {
    markers (newMarkers, oldMarkers) {
      if (!isEqual(newMarkers, oldMarkers)) {
        this.zoomToMarkers()
      }
    }
  },

  mounted () {
    // Add scale to map
    L.control.scale().addTo(this.$refs.map.mapObject)

    // Zoom to display all markers
    this.zoomToMarkers()
  },

  methods: {
    // Zoom and bound map to display all markers
    zoomToMarkers () {
      this.noCoordinates = true
      const defaultBounds = new L.LatLngBounds(L.latLng(-90, 180), L.latLng(90, -180))
      let markerBounds = new L.LatLngBounds()

      // Loop through all markers
      each(this.markers, (marker) => {
        // Stop if no coordinates
        if (marker.coordinates) {
          if (isArray(marker.coordinates) && (marker.coordinates[0] === null || marker.coordinates[1] === null)) return
          if (!isArray(marker.coordinates) && (marker.coordinates.lat === null || marker.coordinates.lng === null)) return
        }
        if (!marker.coordinates && !marker.latitude && !marker.longitude) return

        // Get lat and long
        // GeoJson is [lng, lat, alt]
        const lng = marker.longitude || marker.coordinates.lng || marker.coordinates[0]
        const lat = marker.latitude || marker.coordinates.lat || marker.coordinates[1]

        this.noCoordinates = false
        // markerBounds = new L.LatLngBounds()
        markerBounds.extend(L.latLng(lat, lng))
      })

      this.bounds = markerBounds || defaultBounds
    }
  }
}
</script>

<style scoped lang="scss">
  @import "~@/scss/variables";

  .map-container {
    position: relative;
    height: 100%;

    .no-position-overlay-text {
      position: absolute;
      top: calc(50% - 20px);
      z-index: 1021;
      width: 100%;
      line-height: 20px;
      color: $black;
      text-align: center;
    }

    .no-position-overlay {
      position: absolute;
      z-index: 1020;
      width: 100%;
      height: 100%;
      background: $gray-300;
      border: 1px solid $gray-500;
      border-radius: $border-radius;
      opacity: 0.8;
    }

    .vue2leaflet-map {
      border: 1px solid $gray-500;
      border-radius: $border-radius;
    }

    &.border-0 {
      .vue2leaflet-map {
        border: none;
      }
    }
  }

  .awesome-marker i {
    margin-right: 0;
  }
</style>
